import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthenticationFacade } from '@navfleet/client/authentication/domain/data-access';
import { AppActions } from '@navfleet/client/core/app';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { MapUserLayerExportFileExtension } from '@navfleet/client/map/user-layer/domain/models';
import {
    MAX_FILE_SIZE,
    UserDataLayerNotificationRulesConflictError,
} from '@navfleet/client/map/user-data-layer/domain/models';
import {
    convertResponseToUserDataLayer,
    convertResponseToUserDataLayerObject,
    filterInvalidUserDataLayers,
} from '@navfleet/client/map/user-data-layer/util';
import { ToasterService } from '@navfleet/client/shared/services/toast';
import { downloadFile } from '@navfleet/client/shared/util/functions';
import { TenantFacade, TenantSignalRService } from '@navfleet/client/tenant/domain/data-access';
import { TenantMethods } from '@navfleet/client/tenant/domain/models';
import { TranslationService } from '@navfleet/shared/core/translation';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, mergeMap, of, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { UserDataLayerApi } from '../api/user-data-layer.api';
import { UserDataLayerActions, UserDataLayerAttachmentActions } from './user-data-layer.actions';

@Injectable()
export class UserDataLayerEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(UserDataLayerApi);
    private readonly licenseFacade = inject(LicenseFacade);
    private readonly toasterService = inject(ToasterService);
    private readonly tenantSignalRService = inject(TenantSignalRService);
    private readonly tenantFacade = inject(TenantFacade);
    private readonly authFacade = inject(AuthenticationFacade);
    private readonly store$ = inject(Store);
    private readonly translateService = inject(TranslationService);

    public readonly loadUserDataLayerData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.loadAppData),
            map(() => UserDataLayerActions.loadUserDataLayers())
        )
    );

    public readonly loadUserDataLayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.loadUserDataLayers),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(() =>
                this.api.getAllUserDataLayers().pipe(
                    map((layers) => {
                        const updLayers = layers.map((layer) => convertResponseToUserDataLayer(layer));
                        return UserDataLayerActions.loadUserDataLayersSuccess({ layers: updLayers });
                    }),
                    catchError((error) => of(UserDataLayerActions.loadUserDataLayersFailure({ error })))
                )
            )
        )
    );

    public readonly addUserDataLayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.addUserDataLayer),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ userDataLayer, mapId }) =>
                this.api.addUserDataLayer(userDataLayer).pipe(
                    map((layer) =>
                        UserDataLayerActions.addUserDataLayerSuccess({
                            mapId,
                            layer: convertResponseToUserDataLayer(layer),
                            // keep virtualLayerId to track active user-layer
                            trackingVirtualLayerId: userDataLayer.virtualLayerId as string,
                        })
                    ),
                    catchError((error) => of(UserDataLayerActions.addUserDataLayerFailure({ error })))
                )
            )
        )
    );

    public readonly updateUserDataLayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.updateUserDataLayer),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ userDataLayer, mapId }) =>
                this.api.updateUserDataLayer(userDataLayer).pipe(
                    map((layer) =>
                        UserDataLayerActions.updateUserDataLayerSuccess({
                            layer: convertResponseToUserDataLayer(layer),
                            mapId,
                        })
                    ),
                    catchError((error) => of(UserDataLayerActions.updateUserDataLayerFailure({ error })))
                )
            )
        )
    );

    public readonly userDataLayerNotificationRulesConflictHandler$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    UserDataLayerActions.updateUserDataLayerFailure,
                    UserDataLayerActions.deleteUserDataLayerFailure
                ),
                tap((actionError) => {
                    const response = actionError.error as HttpErrorResponse;
                    if (response.status === 409 && response.error) {
                        const error = response.error as UserDataLayerNotificationRulesConflictError;

                        const ownedBy = this.translateService.instant(
                            'MAP.USER_LAYER.ERROR.USER_DATA_LAYER_NOTIFICATION_RULES_CONFLICT.OWNED_BY'
                        );
                        const rulesString = error.data?.rules?.reduce((acc, rule) => {
                            acc += `${rule.name} - ${ownedBy} ${rule.owner}\n`;
                            return acc;
                        }, ``);

                        this.toasterService.error(
                            'MAP.USER_LAYER.ERROR.USER_DATA_LAYER_NOTIFICATION_RULES_CONFLICT.MESSAGE',
                            { rules: rulesString }
                        );
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly deleteUserDataLayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.deleteUserDataLayer),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ layerId }) =>
                this.api.deleteUserLayer(layerId).pipe(
                    map(() => UserDataLayerActions.deleteUserDataLayerSuccess({ layerId })),
                    catchError((error) => of(UserDataLayerActions.deleteUserDataLayerFailure({ error })))
                )
            )
        )
    );

    public readonly getUserDataLayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.getUserDataLayer),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ layerId }) =>
                this.api.getUserDataLayer(layerId).pipe(
                    map((layer) =>
                        UserDataLayerActions.getUserDataLayerSuccess({ layer: convertResponseToUserDataLayer(layer) })
                    ),
                    catchError((error) => of(UserDataLayerActions.getUserDataLayerFailure({ error })))
                )
            )
        )
    );

    public readonly addUserDataLayerObject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.addUserDataLayerObject),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ object, mapId }) =>
                this.api.addUserDataLayerObject(object).pipe(
                    map((response) =>
                        UserDataLayerActions.addUserDataLayerObjectSuccess({
                            object: convertResponseToUserDataLayerObject(response),
                            mapId,
                            // keep virtualObjectId to track active object
                            trackingVirtualObjectId: object.virtualObjectId!,
                        })
                    ),
                    catchError((error) => of(UserDataLayerActions.addUserDataLayerObjectFailure({ error })))
                )
            )
        )
    );

    public readonly updateUserDataLayerObject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.updateUserDataLayerObject),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ object, mapId }) =>
                this.api.updateUserDataLayerObject(object).pipe(
                    map((response) =>
                        UserDataLayerActions.updateUserDataLayerObjectSuccess({
                            object: convertResponseToUserDataLayerObject(response),
                            mapId,
                            // keep virtualObjectId to track active object
                            trackingVirtualObjectId: object.virtualObjectId!,
                        })
                    ),
                    catchError((error) => of(UserDataLayerActions.updateUserDataLayerObjectFailure({ error })))
                )
            )
        )
    );

    public readonly deleteUserDataLayerObject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.deleteUserDataLayerObject),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            concatMap(({ object }) =>
                this.api.deleteUserDataLayerObject(object).pipe(
                    map(() => UserDataLayerActions.deleteUserDataLayerObjectSuccess({ object })),
                    catchError((error) => of(UserDataLayerActions.deleteUserDataLayerObjectFailure({ object, error })))
                )
            )
        )
    );

    public readonly openAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerAttachmentActions.openAttachment),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            mergeMap((action) =>
                this.api.getAttachment(action.attachment.id).pipe(
                    tap((data) => {
                        downloadFile(data, action.attachment.fileName);
                    }),
                    map(() => UserDataLayerAttachmentActions.openAttachmentSuccess(action)),
                    catchError((error) =>
                        of(
                            UserDataLayerAttachmentActions.openAttachmentFailure({
                                attachment: action.attachment,
                                error,
                            })
                        )
                    )
                )
            )
        )
    );

    public readonly convertFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.convertFile),
            this.licenseFacade.hasLicenseFilter(License.UserLayers),
            mergeMap(({ file }) =>
                this.api.convertFile(file).pipe(
                    map((userDataLayers) =>
                        filterInvalidUserDataLayers(
                            userDataLayers.map((layer) => convertResponseToUserDataLayer(layer, { randomizeId: true }))
                        )
                    ),
                    tap((userDataLayers) => {
                        if (!userDataLayers.length) {
                            this.toasterService.error('USER.DATA_LAYER.FILE_DID_NOT_CONTAIN_VALID_OBJECTS');
                        }
                    }),
                    map((userDataLayers) => UserDataLayerActions.convertFileSuccess({ userDataLayers })),
                    takeUntil(this.actions$.pipe(ofType(UserDataLayerActions.convertFileCancel))),
                    catchError((error) => {
                        if (error?.statusCode === 413) {
                            this.toasterService.error('MAP.MENU.USER_LAYERS.IMPORT.FILE_SIZE_TOO_BIG', {
                                size: MAX_FILE_SIZE,
                                currentSize: (file.size / (1024 * 1024)).toFixed(1),
                            });
                        }

                        return of(UserDataLayerActions.convertFileFailure({ error }));
                    })
                )
            )
        )
    );

    public readonly exportSelectedUserDataLayerObjects$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserDataLayerActions.exportUserDataLayerObjects),
            switchMap(({ layer, objects, exportType }) =>
                this.api
                    .exportUserDataLayerObjects(
                        layer.virtualLayerId,
                        objects.map((object) => object.objectId),
                        exportType
                    )
                    .pipe(
                        map((file) =>
                            UserDataLayerActions.exportUserDataLayerObjectsSuccess({
                                file,
                                name: layer.name as string,
                                exportType,
                            })
                        ),
                        catchError((error) => of(UserDataLayerActions.exportUserDataLayerObjectsFailure({ error })))
                    )
            )
        )
    );

    public readonly exportSelectedUserDataLayerObjectsFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserDataLayerActions.exportUserDataLayerObjectsFailure),
                tap(() => this.toasterService.error('MAP.MENU.USER_LAYERS.EXPORT.UNABLE_TO_EXPORT_OBJECTS'))
            ),
        { dispatch: false }
    );

    public readonly exportSelectedUserDataLayerObjectsSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UserDataLayerActions.exportUserDataLayerObjectsSuccess),
                tap(({ file, name, exportType }) => {
                    downloadFile(file, `${name}${MapUserLayerExportFileExtension[exportType]}`);
                })
            ),
        { dispatch: false }
    );

    public readonly refreshUserDataLayerOnObjectChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                UserDataLayerActions.addUserDataLayerObjectSuccess,
                UserDataLayerActions.updateUserDataLayerObjectSuccess,
                UserDataLayerActions.deleteUserDataLayerObjectSuccess
            ),
            map(({ object }) => UserDataLayerActions.getUserDataLayer({ layerId: object.layerId! }))
        )
    );

    // * SignalR
    public readonly signalRPortalUserDataLayerAdded$ = createEffect(
        () =>
            this.tenantSignalRService.on$(TenantMethods.PortalUserDataLayerAdded).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant(), this.authFacade.userId$),
                tap(([event, tenant, currentUserId]) => {
                    if (event.tenantId === tenant.id) {
                        if (currentUserId === event.userId || (currentUserId !== event.userId && event.isShared)) {
                            this.store$.dispatch(
                                UserDataLayerActions.getUserDataLayer({ layerId: event.userDataLayerId })
                            );
                        }
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRPortalUserDataLayerUpdated$ = createEffect(
        () =>
            this.tenantSignalRService.on$(TenantMethods.PortalUserDataLayerUpdated).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant(), this.authFacade.userId$),
                tap(([event, tenant, currentUserId]) => {
                    if (event.tenantId === tenant.id) {
                        if (currentUserId === event.userId || (currentUserId !== event.userId && event.isShared)) {
                            this.store$.dispatch(
                                UserDataLayerActions.getUserDataLayer({ layerId: event.userDataLayerId })
                            );
                        } else {
                            this.store$.dispatch(
                                UserDataLayerActions.deleteUserDataLayerSuccess({
                                    layerId: event.userDataLayerId,
                                    deleteQuietly: true,
                                })
                            );
                        }
                    }
                })
            ),
        { dispatch: false }
    );

    public readonly signalRPortalUserDataLayerDeleted$ = createEffect(
        () =>
            this.tenantSignalRService.on$(TenantMethods.PortalUserDataLayerDeleted).pipe(
                withLatestFrom(this.tenantFacade.getSelectedTenant(), this.authFacade.userId$),
                tap(([event, tenant, currentUserId]) => {
                    if (event.tenantId === tenant.id) {
                        if (currentUserId === event.userId || (currentUserId !== event.userId && event.isShared)) {
                            this.store$.dispatch(
                                UserDataLayerActions.deleteUserDataLayerSuccess({
                                    layerId: event.userDataLayerId,
                                    deleteQuietly: true,
                                })
                            );
                        }
                    }
                })
            ),
        { dispatch: false }
    );
}
